import { SectionLayout } from "@/components/marketing/SectionLayout";
import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PageHero } from "@/components/marketing/PageHero";
import { Btn, Eyebrow, DisplayHeading } from "@/components/marketing/primitives";
import { HubIndustryGrid } from "@/components/marketing/HubIndustryGrid";
import { ContactCTA } from "@/components/marketing/ContactCTA";
import { EXPANDED_INDUSTRIES } from "@/content/industries";
import { FAQSection } from "@/components/marketing/FAQSection";
import { INDUSTRIES_HUB_FAQS, faqJsonLd } from "@/content/faqs";
import { OG_IMAGE, abs } from "@/content/siteConfig";
import { pushAuditStart } from "@/lib/analytics";


export const Route = createFileRoute("/industries/")({
  head: () => ({
    meta: [
      { title: "Industries: Paid Media for Service & Consumer Brands" },
      {
        name: "description",
        content:
          "Paid media for service businesses, local brands, and growth-focused operators: from home services and trades to consumer goods, F&B, and fitness.",
      },
      { property: "og:title", content: "Industries: Berk Hospitality Group" },
      {
        property: "og:description",
        content:
          "From local service operators to consumer brands, we build paid-media systems engineered for measurable growth.",
      },
      { property: "og:url", content: abs("/industries") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: abs("/industries") }],
    scripts: [
      { type: "application/ld+json", children: faqJsonLd(INDUSTRIES_HUB_FAQS) },
    ],
  }),
  component: IndustriesIndex,
});

function IndustriesIndex() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Industries"
        lede={
          <>
            From local service operators to consumer brands, we build paid-media systems
            engineered for measurable growth: the same operating model, applied across
            categories.
          </>
        }
        ctas={<Btn to="/audit" onClick={() => pushAuditStart("inline")}>Get your free audit →</Btn>}

      >
        Industries we run paid media <span className="display-italic">for.</span>
      </PageHero>

      {/* Linkable detail routes */}
      <section className="bhg-section">
        <SectionLayout>
          <Eyebrow>01, Detail pages</Eyebrow>
          <DisplayHeading as="h2">
            Verticals with <span className="display-italic">deep coverage.</span>
          </DisplayHeading>
          <p
            className="mt-6 max-w-2xl"
            style={{ color: "rgba(0,47,167,0.74)", fontSize: 16, lineHeight: 1.55 }}
          >
            Sixteen trades with documented account structure, channel mix, and buyer-journey
            specifics. Click through for the full thesis on each.
          </p>
          <HubIndustryGrid items={EXPANDED_INDUSTRIES.filter(row => !["10", "11", "12", "13", "14", "15"].includes(row.idx))} />
        </SectionLayout>
      </section>

      {/* Editorial overview of full vertical coverage */}
      <section className="bhg-section" style={{ background: "#f3f1e9" }}>
        <SectionLayout>
          <Eyebrow>02, Full coverage</Eyebrow>
          <DisplayHeading as="h2">
            Categories we&apos;re built <span className="display-italic">to run.</span>
          </DisplayHeading>
          <p
            className="mt-6 max-w-2xl"
            style={{ color: "rgba(0,47,167,0.74)", fontSize: 16, lineHeight: 1.55 }}
          >
            The same operating model, applied across categories.
          </p>

          <HubIndustryGrid items={EXPANDED_INDUSTRIES.filter(row => ["10", "11", "12", "13", "14", "15"].includes(row.idx))} />
        </SectionLayout>
      </section>

      <FAQSection items={INDUSTRIES_HUB_FAQS} />

      <ContactCTA />
    </SiteShell>
  );
}
